import React from "react";
import { useTranslation } from "react-i18next";
import { ColumnColor, colorVars, DivisionResult, divisionWinner, formatAvg } from "./contentTeamShared";
import styles from "./ContentTeam.module.css";

interface Props {
  divisions: string[];
  selected: string;
  onSelect: (division: string) => void;
  /** With exactly two hosts: a dot in the winner's colour on each division tab. */
  hosts?: { name: string; color: ColumnColor }[];
  results?: DivisionResult[];
  /** Pinned to the right end of the row (the guest picker). */
  right?: React.ReactNode;
}

const tabLabel = (division: string): string => {
  if (division === "ligaen") return "Ligaen";
  const m = /^(\d)div([A-D]?)$/.exec(division);
  return m ? `${m[1]}. div${m[2] ? ` ${m[2]}` : ""}` : division;
};

/** The division picker: underline tabs like the match page, one per division. */
const DivisionTabs: React.FC<Props> = ({ divisions, selected, onSelect, hosts, results, right }) => {
  const { t } = useTranslation("pages");
  const byDivision = new Map((results ?? []).map((r) => [r.division, r]));

  return (
    <nav className={styles.divTabs} aria-label={t("contentTeam.division")}>
      <div className={styles.divTabList} role="tablist">
        {divisions.map((d) => {
          const on = d === selected;
          const result = byDivision.get(d);
          const win = hosts && result ? divisionWinner(result.scores) : null;
          let title: string | undefined;
          if (hosts && result && win !== null) {
            const [a, b] = result.scores;
            title =
              win === -1
                ? t("contentTeam.divisionTie", { score: formatAvg(a?.score) })
                : t("contentTeam.divisionWinner", {
                    name: hosts[win].name,
                    won: formatAvg((win === 0 ? a : b)?.score),
                    lost: formatAvg((win === 0 ? b : a)?.score),
                  });
          }
          return (
            <button
              key={d}
              type="button"
              role="tab"
              aria-selected={on}
              className={`${styles.divTab} ${on ? styles.divTabOn : ""}`}
              title={title}
              onClick={() => onSelect(d)}
            >
              {tabLabel(d)}
              {hosts && win !== null && (
                <span
                  className={`${styles.winDot} ${win === -1 ? styles.winDotTie : ""}`}
                  style={win === -1 ? undefined : colorVars(hosts[win].color)}
                  aria-hidden="true"
                />
              )}
            </button>
          );
        })}
      </div>
      {right && <div className={styles.divTabsRight}>{right}</div>}
    </nav>
  );
};

export default DivisionTabs;
